/**
 * Client-side image compression utility.
 * Resizes images before upload to avoid hitting ingress body-size limits and
 * to make storage cheap. Uses the Canvas API — no external deps.
 *
 * - Max edge: 2000px (preserves quality for most covers / portraits)
 * - Output: JPEG quality 0.85 (or PNG if original was PNG with transparency)
 * - Skips files already small enough or non-images
 */
export async function compressImage(file, { maxEdge = 2000, quality = 0.85 } = {}) {
    if (!file || !file.type?.startsWith("image/")) return file;
    // Tiny files: don't bother
    if (file.size < 300 * 1024) return file;

    const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onerror = () => reject(r.error);
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onerror = () => reject(new Error("Falha ao decodificar imagem"));
        im.onload = () => resolve(im);
        im.src = dataUrl;
    });

    let { width, height } = img;
    if (width <= maxEdge && height <= maxEdge && file.size < 1.5 * 1024 * 1024) {
        return file; // already small enough
    }
    if (width > height && width > maxEdge) {
        height = Math.round((height * maxEdge) / width);
        width = maxEdge;
    } else if (height >= width && height > maxEdge) {
        width = Math.round((width * maxEdge) / height);
        height = maxEdge;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    // Preserve PNG for transparency, otherwise JPEG for size
    const isPng = file.type === "image/png";
    const outType = isPng ? "image/png" : "image/jpeg";
    const ext = isPng ? "png" : "jpg";

    const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, outType, isPng ? undefined : quality)
    );
    if (!blob) return file;

    return new File([blob], (file.name?.replace(/\.[^.]+$/, "") || "image") + "." + ext, {
        type: outType,
        lastModified: Date.now(),
    });
}
